import { useState, useEffect } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import OverLayLoader from '../../components/OverLayLoader';
import AddKeywordsForm from '../../components/AddKeywordsForm';
import SubviewContainer from '../../components/SubviewContainer';
import { createWord, getLanguages } from '../../services';
import useTitle from '../../hooks/useTitle';
import { languageOption } from '../../helpers/options';
import PATH from '../../helpers/routes.json';
import { createErrorNotification } from '../../utils/createErrorNotification';

const AddKeywordsView = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState({});

  const { codeId, language } = useParams();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const { t } = useTranslation(['promptbar', 'common']);

  const dictLng = language || languageOption[0];
  const pathTo = state?.from?.pathname
    ? `${state.from.pathname + state.from.search}`
    : PATH.dictionaries.tabs.words;

  const title = t(`promptbar:addKeywords_${dictLng}`);
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      try {
        const langs = await getLanguages();
        const codeData = state?.codeInfo ?? {
          code: searchParams.get('code') || '',
          name: searchParams.get('name') || '',
        };

        if (!codeData.code) {
          throw new Error('Code is unavailable');
        }

        setData(codeData);
        setCurrentLanguage(
          langs.data.find(
            lang => lang.name.toLowerCase() === dictLng.toLowerCase(),
          ),
        );
      } catch (error) {
        createErrorNotification();
        console.log(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [codeId, dictLng, searchParams, state]);

  const formSubmitHandler = async formData => {
    try {
      setIsLoading(true);
      await createWord(formData);
      navigate(pathTo, { state: { language } });
    } catch (error) {
      createErrorNotification();
      console.log(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelHandler = () => {
    navigate(pathTo, { state: { language } });
  };

  return (
    <SubviewContainer title={title}>
      {isLoading ? (
        <OverLayLoader />
      ) : (
        <Box sx={{ padding: '3rem 2rem 2rem' }}>
          <AddKeywordsForm
            tableData={data}
            codeId={Number(codeId)}
            currentLanguage={currentLanguage}
            onCancel={cancelHandler}
            onSubmit={formSubmitHandler}
          />
        </Box>
      )}
    </SubviewContainer>
  );
};

export default AddKeywordsView;
